import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../../i18n';
import { formatMontant, formatDate } from '../../utils/format';
import { Plus, Users, AlertTriangle, CheckCircle, Clock, Printer, DollarSign, RefreshCw, Layers, Search, X, LayoutGrid, List } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { Button, Input, Select } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import PhoneInput from '../../components/common/PhoneInput';
import DatePicker from '../../components/common/DatePicker';
import { useAuth } from '../../context/AuthContext';
import FormSortieMatin from './FormSortieMatin';
import FormRetourSoir from './FormRetourSoir';
import FormClotureCumulee from './FormClotureCumulee';
import FormRegulariserEcart from './FormRegulariserEcart';
import BonSortie from './BonSortie';
import toast from 'react-hot-toast';

// ── Formulaire vendeur ────────────────────────────────────────
const FormVendeur = ({ vendeur, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    nom: vendeur?.nom || '', prenom: vendeur?.prenom || '',
    telephone: vendeur?.telephone || '', statut: vendeur?.statut || 'actif',
    type_remuneration: vendeur?.type_remuneration || 'fixe',
  });
  const [loading, setLoading] = useState(false);
  // Le choix fixe/commission ne s'affiche que si le commerce a activé le mode commission.
  const [modeCommission, setModeCommission] = useState(false);
  useEffect(() => {
    api.get('/parametres')
      .then(r => setModeCommission(r.data?.mode_vendeur === 'commission'))
      .catch(() => setModeCommission(false));
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom) return toast.error('Nom et prénom requis');
    setLoading(true);
    try {
      vendeur ? await api.put(`/vendeurs/${vendeur.id}`, form) : await api.post('/vendeurs', form);
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || t('commun.erreurServeur')); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Prénom *" value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} placeholder="Ex: Mamadou" />
      <Input label="Nom *" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Diallo" />
      <PhoneInput label="Téléphone" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} />
      {modeCommission && (
        <Select label="Rémunération" value={form.type_remuneration}
          onChange={e => setForm({ ...form, type_remuneration: e.target.value })}>
          <option value="fixe">Au fixe (remet 100 %)</option>
          <option value="commission">À la commission</option>
        </Select>
      )}
      {vendeur && (
        <Select label="Statut" value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })}>
          <option value="actif">Actif</option>
          <option value="inactif">Inactif</option>
        </Select>
      )}
      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">{t('commun.annuler')}</Button>
        <Button type="submit" loading={loading} className="flex-1">{vendeur ? t('commun.enregistrer') : t('commun.ajouter')}</Button>
      </div>
    </form>
  );
};

// ── Formulaire remboursement dette ───────────────────────────
const FormRemboursement = ({ vendeur, onSuccess, onCancel }) => {
  const [form, setForm] = useState({ montant: '', mode_paiement: 'especes', notes: '' });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.montant || form.montant <= 0) return toast.error('Montant invalide');
    if (parseFloat(form.montant) > parseFloat(vendeur.solde_dette)) return toast.error('Montant supérieur à la dette');
    setLoading(true);
    try {
      const res = await api.post(`/vendeurs/${vendeur.id}/remboursements`, form);
      toast.success(`Remboursement enregistré — Nouvelle dette : ${formatMontant(res.data.nouvelle_dette)}`);
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || t('commun.erreurServeur')); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4">
        <p className="text-red-300 text-sm">Dette actuelle de <span className="font-bold">{vendeur.prenom} {vendeur.nom}</span></p>
        <p className="text-red-400 font-bold text-2xl mt-1">{formatMontant(vendeur.solde_dette)}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Montant remboursé *</label>
        <InputMontant value={form.montant}
          onChange={v => setForm({ ...form, montant: v })} placeholder="Ex : 50 000"
          className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500" />
      </div>
      <Select label="Mode de paiement" value={form.mode_paiement} onChange={e => setForm({ ...form, mode_paiement: e.target.value })}>
        <option value="especes">💵 Espèces</option>
        <option value="orange_money">🟠 Orange Money</option>
        <option value="mtn_money">🟡 MTN Money</option>
      </Select>
      <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes optionnelles..." />
      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">Annuler</Button>
        <Button type="submit" loading={loading} className="flex-1">Enregistrer le remboursement</Button>
      </div>
    </form>
  );
};

// ── Petite ligne récap clé/valeur (utilisée dans CarteTournee) ────
const Ligne = ({ label, value, valueClass = 'text-white', strong = false }) => (
  <div className="flex items-center justify-between text-xs py-0.5">
    <span className="text-gray-400">{label}</span>
    <span className={`${valueClass} ${strong ? 'font-bold' : 'font-medium'} tabular-nums`}>
      {value}
    </span>
  </div>
);

// Helper : un vendeur est "bloqué" pour nouvelle marchandise si au moins une
// de ses sorties d'un jour antérieur reste ouverte (non régularisée).
// Le backend renvoie cette info via la colonne `nb_sorties_en_souffrance`.
const estVendeurBloque = (v) => Number(v?.nb_sorties_en_souffrance || 0) > 0;

// ── Carte d'une tournée individuelle ──────────────────────────
const CarteTournee = ({ sortie, vendeur, onRetour, onBon, onNouvelleTournee, onRembourser, onRegulariser, estDerniere = false }) => {
  const tourneeLabel = sortie.numero_tournee > 1
    ? `Tournée #${sortie.numero_tournee}`
    : 'Tournée du matin';
  const estCloturee  = sortie.statut === 'cloturee' || !!sortie.retour_id;
  const ecart        = Number(sortie.ecart) || 0;
  const aEcart       = ecart < 0;
  const estCumulee   = !!sortie.cloture_groupee_id;

  // Valeurs clés (depuis l'API enrichie)
  const valeurSortie    = Number(sortie.montant_total_attendu || 0);
  const valeurRetournee = Number(sortie.valeur_retournee || 0);
  const valeurVendue    = valeurSortie - valeurRetournee;
  const montantRemis    = Number(sortie.montant_remis || 0);

  // État de la dette liée à cet écart
  const detteRestante  = Number(sortie.dette_montant_restant || 0);
  const detteSoldee    = sortie.dette_id && sortie.dette_statut === 'soldee';
  const dettePartielle = sortie.dette_id && sortie.dette_statut === 'partielle';
  const detteEnCours   = sortie.dette_id
                      && sortie.dette_statut !== 'soldee'
                      && sortie.dette_statut !== 'radiee'
                      && detteRestante > 0;

  // Style du contour
  const borderClass = detteEnCours
    ? 'border-red-700/50 bg-red-900/10'
    : estCloturee
      ? 'border-green-700/30 bg-green-900/10'
      : 'border-yellow-600/30 bg-gray-800/40';

  return (
    <div className={`border rounded-xl p-4 transition-all ${borderClass}`}>
      {/* En-tête : nom vendeur + libellé tournée + badge statut */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white flex items-center gap-1.5">
            {vendeur.prenom} {vendeur.nom}
          </span>
          <span className="text-gray-600">·</span>
          <span className="text-xs font-semibold text-gray-400 bg-gray-800 px-2 py-0.5 rounded-full">
            {tourneeLabel}
          </span>
          {estCumulee && (
            <span className="text-[10px] text-yellow-400 bg-yellow-600/10 border border-yellow-600/30 px-1.5 py-0.5 rounded-full">
              Clôture cumulée
            </span>
          )}
        </div>
        <Badge variant={
          detteEnCours ? 'red'
          : estCloturee ? 'green'
          : 'yellow'
        }>
          {detteEnCours
            ? `⚠️ Reste ${formatMontant(detteRestante)}`
            : estCloturee
              ? '✅ Clôturé'
              : '🟡 En cours'}
        </Badge>
      </div>

      {/* Détail des valeurs */}
      <div className="bg-gray-800/40 rounded-lg p-3 space-y-0.5 mb-3">
        <Ligne
          label={`Valeur sortie (${sortie.total_pieces_confiees || 0} pcs confiées)`}
          value={formatMontant(valeurSortie)}
          valueClass="text-yellow-400"
        />
        {estCloturee && (
          <>
            <Ligne
              label="Valeur retournée"
              value={formatMontant(valeurRetournee)}
              valueClass="text-blue-400"
            />
            <Ligne
              label="Valeur vendue"
              value={formatMontant(valeurVendue)}
              valueClass="text-green-400"
              strong
            />
            <div className="border-t border-gray-700/50 my-1.5" />
            <Ligne
              label="Montant remis"
              value={formatMontant(montantRemis)}
              valueClass="text-white"
            />
            {/* Ligne « Écart » — la valeur historique reste visible pour
                la traçabilité comptable, mais le style change selon l'état
                de régularisation de la dette associée. */}
            <div className="flex items-center justify-between text-xs py-0.5">
              <span className="text-gray-400">Écart</span>
              <span className={`font-bold tabular-nums ${
                detteSoldee
                  ? 'text-gray-500 line-through decoration-2'
                  : ecart === 0   ? 'text-green-400'
                  : dettePartielle ? 'text-orange-400'
                  : ecart < 0     ? 'text-red-400'
                  : 'text-orange-400'
              }`}>
                {ecart > 0 ? '+' : ''}{formatMontant(ecart)}
              </span>
            </div>

            {/* Indicateur de régularisation (sous la ligne Écart) */}
            {detteSoldee && (
              <div className="flex items-center justify-end gap-1 text-[10px] text-green-400 font-semibold mt-0.5">
                <CheckCircle size={10} />
                <span>Écart régularisé entièrement</span>
              </div>
            )}
            {dettePartielle && (
              <div className="flex items-center justify-end gap-1 text-[10px] text-orange-300 mt-0.5">
                <span>Régularisation partielle — reste {formatMontant(detteRestante)}</span>
              </div>
            )}

            {sortie.explication_ecart && (
              <div className="mt-2 pt-2 border-t border-gray-700/40">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Explication</p>
                <p className="text-xs text-gray-300 italic">« {sortie.explication_ecart} »</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700/50">
        <button onClick={() => onBon(sortie)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-xs transition-colors">
          <Printer size={12} /><span>Bon</span>
        </button>

        {!estCloturee && (
          <>
            <button onClick={() => onRetour(sortie)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-yellow-600 hover:bg-yellow-500 text-black rounded-lg text-xs font-semibold transition-colors">
              <CheckCircle size={13} /><span>Retour du soir</span>
            </button>
            {/* "+ Tournée" uniquement sur la carte la plus récente ET si vendeur non bloqué */}
            {estDerniere && !estVendeurBloque(vendeur) && (
              <button onClick={() => onNouvelleTournee(vendeur)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700/30 hover:bg-blue-700/50 text-blue-400 rounded-lg text-xs transition-colors">
                <Plus size={12} /><span>+ Tournée</span>
              </button>
            )}
          </>
        )}

        {/* Régularisation : prioritaire sur "Nouvelle tournée" si dette en cours */}
        {estCloturee && detteEnCours && (
          <button onClick={() => onRegulariser(sortie)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-bold transition-colors"
            title={sortie.dette_est_cumulee
              ? `Régulariser l'écart de la clôture cumulée (dette partagée entre plusieurs sorties)`
              : 'Le vendeur a apporté tout ou partie de l\'argent manquant'}>
            <DollarSign size={13} />
            <span>
              Régulariser {sortie.dette_est_cumulee ? '(cumulée)' : 'l\'écart'} ({formatMontant(detteRestante)})
            </span>
          </button>
        )}

        {/* "+ Nouvelle tournée" uniquement sur la carte la plus récente ET si vendeur non bloqué */}
        {estCloturee && !detteEnCours && estDerniere && !estVendeurBloque(vendeur) && (
          <button onClick={() => onNouvelleTournee(vendeur)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700/30 hover:bg-blue-700/50 text-blue-400 rounded-lg text-xs transition-colors">
            <Plus size={12} /><span>+ Nouvelle tournée</span>
          </button>
        )}

        {/* Le bouton "Rembourser solde" a été retiré d'ici : il faisait
            doublon avec "Régulariser l'écart" qui, depuis le fix FIFO,
            réduit déjà la dette individuelle ET le solde global du vendeur.
            Pour rembourser une dette d'un vendeur sans tournée du jour
            (cas rare), on utilise le bouton "Rembourser" dans la vue tableau
            de l'onglet "Vendeurs". */}
      </div>
    </div>
  );
};

// ── Carte vendeur groupant ses tournées ──────────────────────
const CarteVendeur = ({ vendeur, tournees, onNouvelleTournee, onRetour, onBon, onRembourser, onClotureCumulee, onRegulariser }) => {
  // Sorties ouvertes (non encore clôturées) du vendeur — c'est ce qu'on peut
  // clôturer en une fois via le mode cumulé.
  const sortiesOuvertes = tournees.filter(s => s.statut !== 'cloturee' && !s.retour_id);
  // Le mode cumulé n'a vraiment de sens qu'à partir de 2 sorties ouvertes
  // (sinon le mode par-sortie classique suffit).
  // La clôture cumulée (multi-sorties d'un coup) ne gère pas la saisie de
  // commission par article : on la réserve aux vendeurs au fixe. Un vendeur
  // à la commission clôture sortie par sortie (formulaire dédié).
  const peutCloturerCumule = sortiesOuvertes.length >= 2 && vendeur.type_remuneration !== 'commission';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
      {/* En-tête vendeur */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white font-semibold flex items-center gap-2">
            {vendeur.prenom} {vendeur.nom}
            {vendeur.type_remuneration === 'commission' && (
              <span className="text-[10px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
                À la commission
              </span>
            )}
          </p>
          {vendeur.telephone && <p className="text-gray-500 text-xs">{vendeur.telephone}</p>}
          {Number(vendeur.solde_dette) > 0 && (
            <p className="text-red-400 text-xs mt-0.5">💸 Dette : {formatMontant(vendeur.solde_dette)}</p>
          )}
          {estVendeurBloque(vendeur) && (
            <p className="text-red-400 text-xs mt-0.5 font-semibold">
              🚫 Bloqué — {vendeur.nb_sorties_en_souffrance} sortie{Number(vendeur.nb_sorties_en_souffrance) > 1 ? 's' : ''} en souffrance
            </p>
          )}
        </div>
        <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
          {tournees.length} tournée{tournees.length > 1 ? 's' : ''} ce jour
        </span>
      </div>

      {/* Bouton clôture cumulée — visible si 2+ sorties ouvertes */}
      {peutCloturerCumule && (
        <button
          onClick={() => onClotureCumulee(vendeur)}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-black font-bold rounded-lg text-sm transition-all shadow-md"
          title="Clôturer toutes les sorties ouvertes en une seule opération de fin de journée"
        >
          <Layers size={16} />
          <span>Clôturer la journée ({sortiesOuvertes.length} tournées en attente)</span>
        </button>
      )}

      {/* Tournées du jour */}
      <div className="space-y-2">
        {tournees.map((s, i) => (
          <CarteTournee
            key={s.id}
            sortie={s}
            vendeur={vendeur}
            estDerniere={i === tournees.length - 1}
            onRetour={onRetour}
            onBon={onBon}
            onNouvelleTournee={onNouvelleTournee}
            onRembourser={onRembourser}
            onRegulariser={onRegulariser}
          />
        ))}
      </div>
    </div>
  );
};

// ── Page principale ───────────────────────────────────────────
export default function Vendeurs() {
  const { canEditBusiness } = useAuth();
  const [vendeurs, setVendeurs] = useState([]);
  const [sortiesJour, setSortiesJour] = useState([]);
  const [sortiesEnSouffrance, setSortiesEnSouffrance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onglet, setOnglet] = useState('journee');
  const [modalCreer, setModalCreer] = useState(false);
  const [vendeurModif, setVendeurModif] = useState(null);
  const [vendeurSortie, setVendeurSortie] = useState(null);
  const [sortieRetour, setSortieRetour] = useState(null);
  const [vendeurCloture, setVendeurCloture] = useState(null); // clôture cumulée
  const [sortieRegulariser, setSortieRegulariser] = useState(null); // régularisation écart
  const [sortieBon, setSortieBon] = useState(null);
  const [vendeurRemb, setVendeurRemb] = useState(null);
  const [recherche, setRecherche] = useState('');
  // Date sélectionnée pour consulter les sorties d'un jour donné.
  // Défaut : aujourd'hui. Modifiable pour audit/historique.
  const [dateStr, setDateStr] = useState(new Date().toISOString().split('T')[0]);
  const aujourdhui = new Date().toISOString().split('T')[0];
  const estAujourdhui = dateStr === aujourdhui;

  // Mode d'affichage de l'onglet « Journée » : cartes (par défaut) ou liste.
  // Persisté en localStorage comme pour Fournisseurs.
  const [vueJournee, setVueJournee] = useState(() => {
    try { return localStorage.getItem('vendeurs_vue_journee') || 'cartes'; }
    catch { return 'cartes'; }
  });
  useEffect(() => {
    try { localStorage.setItem('vendeurs_vue_journee', vueJournee); } catch { /* ignore */ }
  }, [vueJournee]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, sRes, esRes] = await Promise.all([
        api.get('/vendeurs'),
        api.get('/vendeurs/sorties/jour', { params: { date: dateStr } }),
        api.get('/vendeurs/sorties/en-souffrance'),
      ]);
      setVendeurs(vRes.data);
      setSortiesJour(sRes.data);
      setSortiesEnSouffrance(esRes.data || []);
    } catch { toast.error(t('commun.erreurServeur')); }
    finally { setLoading(false); }
  }, [dateStr]);

  useEffect(() => { charger(); }, [charger]);

  const handleSuccess = (msg) => {
    setModalCreer(false); setVendeurModif(null); setVendeurSortie(null);
    setSortieRetour(null); setVendeurRemb(null); setVendeurCloture(null);
    setSortieRegulariser(null);
    charger();
    if (msg) toast.success(msg);
  };

  // Filtre de recherche (prénom, nom, téléphone)
  const rechercheNorm = recherche.trim().toLowerCase();
  const vendeursFiltres = rechercheNorm
    ? vendeurs.filter(v => {
        const hay = [v.prenom, v.nom, v.telephone].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(rechercheNorm);
      })
    : vendeurs;

  const vendeursActifs    = vendeursFiltres.filter(v => v.statut === 'actif');
  const vendeursAvecDette = vendeursFiltres.filter(v => Number(v.solde_dette) > 0);

  // Grouper les sorties du jour par vendeur
  const sortiesParVendeur = sortiesJour.reduce((acc, s) => {
    if (!acc[s.vendeur_id]) acc[s.vendeur_id] = [];
    acc[s.vendeur_id].push(s);
    return acc;
  }, {});

  const vendeursAvecSortie = vendeursActifs.filter(v => sortiesParVendeur[v.id]?.length > 0);
  const vendeursSansSortie = vendeursActifs.filter(v => !sortiesParVendeur[v.id]?.length);

  // Totaux du jour
  const totalAttendu  = sortiesJour.reduce((s, x) => s + Number(x.montant_total_attendu || 0), 0);
  const totalEncaisse = sortiesJour.filter(x => x.retour_id).reduce((s, x) => s + Number(x.montant_remis || 0), 0);
  const totalEcarts   = sortiesJour.filter(x => Number(x.ecart) < 0).reduce((s, x) => s + Math.abs(Number(x.ecart || 0)), 0);

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('vendeurs.titre')}</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {formatDate(dateStr)}
            {!estAujourdhui && <span className="ml-2 text-yellow-400">(consultation historique)</span>}
            {' — '}{vendeursActifs.length} vendeur(s) actif(s)
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={charger} className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded-lg transition-colors" title="Actualiser">
            <RefreshCw size={16} />
          </button>
          {canEditBusiness && <Button onClick={() => setModalCreer(true)}><Plus size={16} /><span>{t('vendeurs.ajouterVendeur')}</span></Button>}
        </div>
      </div>

      {/* Navigation date — consulter un jour précédent pour audit/historique */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px] max-w-md">
          <DatePicker
            label="Date consultée"
            value={dateStr}
            max={aujourdhui}
            onChange={e => setDateStr(e.target.value)}
          />
        </div>
        {!estAujourdhui && (
          <button
            onClick={() => setDateStr(aujourdhui)}
            className="self-end px-3 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-medium rounded-lg transition-colors"
          >
            ↩ Revenir à aujourd'hui
          </button>
        )}
      </div>

      {/* KPIs du jour */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Tournées du jour',  value: sortiesJour.length,          color: 'text-blue-400' },
          { label: 'Montant attendu',   value: formatMontant(totalAttendu),  color: 'text-yellow-400' },
          { label: 'Montant encaissé',  value: formatMontant(totalEncaisse), color: 'text-green-400' },
          { label: '⚠️ Écarts du jour', value: formatMontant(totalEcarts),   color: totalEcarts > 0 ? 'text-red-400' : 'text-gray-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-400 text-xs">{label}</p>
            <p className={`font-bold text-lg mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Alerte dettes */}
      {vendeursAvecDette.length > 0 && (
        <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0" />
          <p className="text-red-300 text-sm">
            <span className="font-bold">{vendeursAvecDette.length} vendeur(s)</span> ont des dettes impayées —
            total : <span className="font-bold text-red-400">
              {formatMontant(vendeursAvecDette.reduce((s, v) => s + Number(v.solde_dette), 0))}
            </span>
          </p>
        </div>
      )}

      {/* Onglets + Recherche + sélecteur de vue */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex space-x-1 bg-gray-900 p-1 rounded-xl w-fit">
          {[{ key: 'journee', label: '📅 Journée' }, { key: 'vendeurs', label: '👥 Vendeurs' }].map(({ key, label }) => (
            <button key={key} onClick={() => setOnglet(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${onglet === key ? 'bg-yellow-600 text-black' : 'text-gray-400 hover:text-white'}`}>
              {label}
            </button>
          ))}
        </div>
        {vendeurs.length > 0 && (
          <div className="relative flex-1 sm:max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={recherche}
              onChange={e => setRecherche(e.target.value)}
              placeholder="Rechercher un vendeur (prénom, nom, téléphone...)"
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-9 py-2 focus:outline-none focus:border-yellow-500 transition-colors placeholder-gray-600 text-sm"
            />
            {recherche && (
              <button
                type="button"
                onClick={() => setRecherche('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white rounded transition-colors"
                title="Effacer la recherche"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
        {/* Sélecteur de vue : cartes ↔ liste (visible uniquement sur "Journée") */}
        {onglet === 'journee' && sortiesJour.length > 0 && (
          <div className="inline-flex bg-gray-800 border border-gray-700 rounded-lg p-0.5 sm:ml-auto">
            <button
              type="button"
              onClick={() => setVueJournee('cartes')}
              title="Vue cartes"
              className={`p-1.5 rounded-md transition-colors ${
                vueJournee === 'cartes' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              onClick={() => setVueJournee('liste')}
              title="Vue liste compacte"
              className={`p-1.5 rounded-md transition-colors ${
                vueJournee === 'liste' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              <List size={15} />
            </button>
          </div>
        )}
      </div>

      {/* ── VUE JOURNÉE ── */}
      {onglet === 'journee' && (
        <div className="space-y-4">
          {/* ⚠️ Sorties en souffrance — non régularisées des jours précédents */}
          {sortiesEnSouffrance.length > 0 && (
            <div className="bg-red-900/20 border border-red-700/60 rounded-xl p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-bold text-sm">
                    {sortiesEnSouffrance.length} sortie{sortiesEnSouffrance.length > 1 ? 's' : ''} en souffrance — à régulariser
                  </p>
                  <p className="text-red-200/80 text-xs mt-1">
                    Ces sorties datent de jours précédents et bloquent les vendeurs concernés.
                    Clique sur « Régler » dès que le vendeur revient avec l'argent et/ou les articles invendus.
                    Une fois réglée, le vendeur pourra reprendre une sortie immédiatement.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {sortiesEnSouffrance.map(s => (
                  <div key={s.id} className="bg-gray-900/60 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-medium text-sm truncate">{s.prenom} {s.nom}</p>
                        <Badge variant="red">
                          {s.anciennete_jours === 1 ? 'hier' : `il y a ${s.anciennete_jours}j`}
                        </Badge>
                        {s.numero_tournee > 1 && (
                          <span className="text-xs text-gray-500">Tournée #{s.numero_tournee}</span>
                        )}
                        {Number(s.nb_reports) > 0 && (
                          <span className="text-[10px] text-orange-400">
                            ({s.nb_reports} report{s.nb_reports > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {s.date} · {s.total_pieces_confiees} pcs · valeur {formatMontant(s.montant_total_attendu)}
                      </p>
                    </div>
                    <button
                      onClick={() => setSortieRetour({
                        id: s.id, prenom: s.prenom, nom: s.nom,
                        numero_tournee: s.numero_tournee, vendeur_id: s.vendeur_id,
                      })}
                      className="flex items-center gap-1.5 px-3 py-2 bg-yellow-600 hover:bg-yellow-500 text-black rounded-lg text-xs font-bold transition-colors flex-shrink-0"
                      title="Faire le retour du soir pour cette sortie"
                    >
                      <CheckCircle size={13} />
                      <span>Régler maintenant</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vendeurs sans aucune sortie */}
          {vendeursSansSortie.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-400 mb-3">
                Sans sortie aujourd'hui ({vendeursSansSortie.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {vendeursSansSortie.map(v => {
                  const bloque = estVendeurBloque(v);
                  return (
                    <button
                      key={v.id}
                      onClick={() => !bloque && setVendeurSortie(v)}
                      disabled={bloque}
                      title={bloque
                        ? `Bloqué : ${v.nb_sorties_en_souffrance} sortie(s) en souffrance à régulariser`
                        : `Nouvelle sortie pour ${v.prenom} ${v.nom}`}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                        bloque
                          ? 'bg-red-900/20 border-red-700/40 text-red-400/70 cursor-not-allowed line-through'
                          : 'bg-yellow-600/20 hover:bg-yellow-600/40 border-yellow-600/30 text-yellow-400'
                      }`}
                    >
                      {bloque ? <span>🚫</span> : <Plus size={14} />}
                      <span>{v.prenom} {v.nom}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aucune sortie du tout */}
          {sortiesJour.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-32 space-y-2">
              <Clock size={32} className="text-gray-600" />
              <p className="text-gray-500 text-sm">Aucune sortie enregistrée aujourd'hui</p>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
            </div>
          )}

          {/* Cartes groupées par vendeur — layout colonnes Masonry
              On utilise CSS Columns plutôt que Grid pour que chaque carte
              prenne uniquement la hauteur de son contenu. Sans ça, un vendeur
              avec 1 tournée laissait un grand espace vide à côté d'un voisin
              qui en avait 3 (Grid aligne les hauteurs par rangée).
              `break-inside-avoid` empêche une carte d'être coupée en deux
              colonnes ; `mb-4` reproduit le `gap` (qui ne s'applique pas aux
              colonnes CSS classiques). */}
          {!loading && vendeursAvecSortie.length > 0 && (
            vueJournee === 'liste' ? (
              /* ── Vue LISTE : une ligne par tournée, dense ────────────── */
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-auto max-h-[70vh]">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-gray-900 sticky top-0 z-10">
                    <tr className="text-xs text-gray-400 uppercase tracking-wide">
                      <th className="text-left px-3 py-3">Vendeur</th>
                      <th className="text-left px-3 py-3">Tournée</th>
                      <th className="text-right px-3 py-3 hidden md:table-cell">Valeur sortie</th>
                      <th className="text-right px-3 py-3 hidden lg:table-cell">Retour</th>
                      <th className="text-right px-3 py-3">Remis</th>
                      <th className="text-right px-3 py-3">Écart</th>
                      <th className="text-center px-3 py-3">Statut</th>
                      <th className="text-center px-3 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {sortiesJour.filter(s => sortiesParVendeur[s.vendeur_id])
                      .sort((a, b) => {
                        // Grouper par vendeur, puis par numéro de tournée
                        const va = vendeursAvecSortie.find(v => v.id === a.vendeur_id);
                        const vb = vendeursAvecSortie.find(v => v.id === b.vendeur_id);
                        const cmp = `${va?.prenom || ''} ${va?.nom || ''}`.localeCompare(`${vb?.prenom || ''} ${vb?.nom || ''}`);
                        if (cmp !== 0) return cmp;
                        return (a.numero_tournee || 0) - (b.numero_tournee || 0);
                      })
                      .map(s => {
                        const v = vendeursAvecSortie.find(x => x.id === s.vendeur_id);
                        if (!v) return null;
                        const estCloturee = s.statut === 'cloturee' || !!s.retour_id;
                        const ecart = Number(s.ecart) || 0;
                        const detteRestante = Number(s.dette_montant_restant || 0);
                        const detteEnCours = s.dette_id && s.dette_statut !== 'soldee' && s.dette_statut !== 'radiee' && detteRestante > 0;
                        return (
                          <tr key={s.id} className="hover:bg-gray-800/40">
                            <td className="px-3 py-2.5 font-medium text-white">{v.prenom} {v.nom}</td>
                            <td className="px-3 py-2.5 text-gray-300">
                              {s.numero_tournee > 1 ? `#${s.numero_tournee}` : 'Matin'}
                              {!!s.cloture_groupee_id && (
                                <span className="ml-1.5 text-[9px] text-yellow-400 bg-yellow-600/10 border border-yellow-600/30 px-1 py-0.5 rounded">cumul</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right text-yellow-400 hidden md:table-cell tabular-nums">
                              {formatMontant(s.montant_total_attendu)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-blue-400 hidden lg:table-cell tabular-nums">
                              {estCloturee ? formatMontant(s.valeur_retournee) : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-white tabular-nums">
                              {estCloturee ? formatMontant(s.montant_remis) : '—'}
                            </td>
                            <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${
                              !estCloturee ? 'text-gray-600'
                              : ecart === 0 ? 'text-green-400'
                              : detteEnCours ? 'text-red-400'
                              : 'text-orange-400'
                            }`}>
                              {estCloturee ? `${ecart > 0 ? '+' : ''}${formatMontant(ecart)}` : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <Badge variant={detteEnCours ? 'red' : estCloturee ? 'green' : 'yellow'}>
                                {detteEnCours ? '⚠️ Dette' : estCloturee ? '✅ OK' : '🟡 Ouvert'}
                              </Badge>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-center gap-1 flex-wrap">
                                <button
                                  onClick={() => setSortieBon(s)}
                                  title="Imprimer le bon"
                                  className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-md transition-colors"
                                >
                                  <Printer size={13} />
                                </button>
                                {!estCloturee && (
                                  <button
                                    onClick={() => setSortieRetour({ id: s.id, prenom: v.prenom, nom: v.nom, numero_tournee: s.numero_tournee, vendeur_id: v.id })}
                                    title="Retour du soir"
                                    className="p-1.5 bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400 rounded-md transition-colors"
                                  >
                                    <CheckCircle size={13} />
                                  </button>
                                )}
                                {detteEnCours && (
                                  <button
                                    onClick={() => setSortieRegulariser({ ...s, vendeur: v })}
                                    title={`Régulariser ${formatMontant(detteRestante)}`}
                                    className="p-1.5 bg-orange-600/30 hover:bg-orange-600/50 text-orange-300 rounded-md transition-colors"
                                  >
                                    <DollarSign size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* ── Vue CARTES (Masonry) ──────────────────────────────────── */
              <div className="columns-1 lg:columns-2 gap-4 [&>div]:break-inside-avoid [&>div]:mb-4">
                {vendeursAvecSortie.map(v => (
                  <CarteVendeur
                    key={v.id}
                    vendeur={v}
                    tournees={sortiesParVendeur[v.id] || []}
                    onNouvelleTournee={setVendeurSortie}
                    onRetour={setSortieRetour}
                    onBon={setSortieBon}
                    onRembourser={setVendeurRemb}
                    onClotureCumulee={setVendeurCloture}
                    onRegulariser={(sortie) => setSortieRegulariser({ ...sortie, vendeur: v })}
                  />
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* ── VUE TOUS LES VENDEURS ── */}
      {onglet === 'vendeurs' && (
        <div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
            </div>
          ) : vendeurs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 space-y-3">
              <Users size={40} className="text-gray-600" />
              <p className="text-gray-500">Aucun vendeur enregistré</p>
              <Button onClick={() => setModalCreer(true)}><Plus size={16} /><span>Ajouter un vendeur</span></Button>
            </div>
          ) : vendeursFiltres.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 space-y-2">
              <Search size={28} className="text-gray-600" />
              <p className="text-gray-500 text-sm">Aucun vendeur ne correspond à « {recherche} »</p>
              <button onClick={() => setRecherche('')} className="text-yellow-400 hover:text-yellow-300 text-xs underline">
                Effacer la recherche
              </button>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-auto max-h-[70vh]">
              <table className="w-full min-w-[640px]">
                <thead className="sticky top-0 z-10 bg-gray-900">
                  <tr className="border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Vendeur</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase hidden sm:table-cell">Statut</th>
                    <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Dette</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase hidden md:table-cell">Auj.</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase hidden lg:table-cell">Total</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {vendeursFiltres.map(v => (
                    <tr key={v.id} className="hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{v.prenom} {v.nom}</p>
                        {v.telephone && <p className="text-gray-500 text-xs">{v.telephone}</p>}
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <Badge variant={v.statut === 'actif' ? 'green' : 'gray'}>{v.statut}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {Number(v.solde_dette) > 0
                          ? <span className="text-red-400 font-bold text-sm">{formatMontant(v.solde_dette)}</span>
                          : <span className="text-gray-600 text-sm">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        {Number(v.nb_sorties_jour) > 0
                          ? <span className="text-blue-400 font-semibold">{v.nb_sorties_jour}</span>
                          : <span className="text-gray-600">0</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400 text-sm hidden lg:table-cell">
                        {v.nb_sorties_total || 0}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button
                            onClick={() => !estVendeurBloque(v) && setVendeurSortie(v)}
                            disabled={estVendeurBloque(v)}
                            title={estVendeurBloque(v)
                              ? `Bloqué : ${v.nb_sorties_en_souffrance} sortie(s) en souffrance`
                              : 'Nouvelle sortie'}
                            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                              estVendeurBloque(v)
                                ? 'bg-red-900/20 text-red-400/60 cursor-not-allowed'
                                : 'bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400'
                            }`}
                          >
                            {estVendeurBloque(v)
                              ? <span>🚫</span>
                              : <Plus size={11} />}
                            <span className="hidden sm:inline">Sortie</span>
                          </button>
                          <button onClick={() => setVendeurModif(v)}
                            className="px-2 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-xs transition-colors">
                            Modifier
                          </button>
                          {Number(v.solde_dette) > 0 && (
                            <button onClick={() => setVendeurRemb(v)}
                              className="flex items-center gap-1 px-2 py-1.5 bg-red-700/30 hover:bg-red-700/50 text-red-400 rounded-lg text-xs transition-colors">
                              <DollarSign size={11} /><span className="hidden sm:inline">Remb.</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      <Modal open={modalCreer} onClose={() => setModalCreer(false)} title="Ajouter un vendeur" size="sm">
        <FormVendeur onSuccess={() => handleSuccess('Vendeur ajouté')} onCancel={() => setModalCreer(false)} />
      </Modal>

      <Modal open={!!vendeurModif} onClose={() => setVendeurModif(null)}
        title={`Modifier — ${vendeurModif?.prenom} ${vendeurModif?.nom}`} size="sm">
        {vendeurModif && <FormVendeur vendeur={vendeurModif} onSuccess={() => handleSuccess('Vendeur modifié')} onCancel={() => setVendeurModif(null)} />}
      </Modal>

      <Modal open={!!vendeurSortie} onClose={() => setVendeurSortie(null)}
        title={`Nouvelle sortie — ${vendeurSortie?.prenom} ${vendeurSortie?.nom}`} size="lg">
        {vendeurSortie && (
          <FormSortieMatin vendeur={vendeurSortie} onSuccess={() => handleSuccess('Sortie enregistrée')} onCancel={() => setVendeurSortie(null)} />
        )}
      </Modal>

      <Modal open={!!sortieRetour} onClose={() => setSortieRetour(null)}
        title={`Retour du soir — ${sortieRetour?.prenom} ${sortieRetour?.nom}${sortieRetour?.numero_tournee > 1 ? ` (Tournée #${sortieRetour?.numero_tournee})` : ''}`}
        size="lg">
        {sortieRetour && <FormRetourSoir sortie={sortieRetour} onSuccess={() => handleSuccess('Retour enregistré')} onCancel={() => setSortieRetour(null)} />}
      </Modal>

      <Modal open={!!sortieBon} onClose={() => setSortieBon(null)} title="Bon de sortie" size="md">
        {sortieBon && <BonSortie sortieId={sortieBon.id} onClose={() => setSortieBon(null)} />}
      </Modal>

      <Modal open={!!vendeurCloture} onClose={() => setVendeurCloture(null)}
        title={`Clôture cumulée — ${vendeurCloture?.prenom} ${vendeurCloture?.nom}`} size="lg">
        {vendeurCloture && (
          <FormClotureCumulee
            vendeur={vendeurCloture}
            onSuccess={() => handleSuccess('Clôture cumulée enregistrée')}
            onCancel={() => setVendeurCloture(null)}
          />
        )}
      </Modal>

      <Modal open={!!sortieRegulariser} onClose={() => setSortieRegulariser(null)}
        title="Régulariser un écart" size="sm">
        {sortieRegulariser && (
          <FormRegulariserEcart
            sortie={sortieRegulariser}
            vendeur={sortieRegulariser.vendeur}
            onSuccess={() => handleSuccess('Écart régularisé')}
            onCancel={() => setSortieRegulariser(null)}
          />
        )}
      </Modal>

      <Modal open={!!vendeurRemb} onClose={() => setVendeurRemb(null)}
        title={`Remboursement — ${vendeurRemb?.prenom} ${vendeurRemb?.nom}`} size="sm">
        {vendeurRemb && <FormRemboursement vendeur={vendeurRemb} onSuccess={() => handleSuccess('Remboursement enregistré')} onCancel={() => setVendeurRemb(null)} />}
      </Modal>
    </div>
  );
}
